import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import AppShell, { storedSession } from '../components/AppShell';
import { ActionBar, AppStatus, iconBtn, LogDialog, MoreMenu, ownerText, Stepper, TransferBanner } from '../components/candidates/ApplicationCard';
import { canUseCandidates, Chip, DuplicateBadges, isAdmin, place, SOURCE_LABEL, WORK_AUTH } from '../components/candidates/common';
import { MergeDialog } from '../components/candidates/DuplicatesPanel';
import { JobPicker } from '../components/candidates/CandidateList';
import { AuthenticityPanel, GradeTile, MatchPanel, RiskBadge } from '../components/candidates/MatchPanel';
import { ActivityPanel, OverviewTab, ProfileTab, ScreensTab } from '../components/candidates/ProfileTabs';
import { api, btnDanger, btnPrimary, btnSecondary, card, InlineError, input, labelCls, Modal, Skeleton, Tabs, ToastProvider, useToast } from '../components/ui/Kit';
import { downloadPrivacyData, downloadProfilePdf } from '../utils/profileExport';

// /candidates/:id — the candidate profile (bundle 4e). A sticky header: who they are, the grade for the job being
// viewed, authenticity, owner, contacts, "Viewing for: [job ▾]" (only jobs the viewer can access — it drives the
// stepper, the actions and the Match tab), the stage stepper and one action bar. Tabs (?tab=, the last one remembered,
// keys 1–5): Overview · Match · Profile · Screens & interviews · Authenticity (⚠ = items to verify). The activity
// panel sits on the right (collapsible, remembered) and becomes a sixth tab under 1200 px. Each tab loads when first
// opened. Exports live in "⋯": Download profile (PDF) and, for company admins, Download data (privacy request).

const TAB_KEYS = ['overview', 'match', 'profile', 'screens', 'authenticity', 'activity'];
const LAST_TAB = 'q5d.profile.tab';
const PANEL_OPEN = 'q5d.profile.activityOpen';
const read = (k) => { try { return window.localStorage.getItem(k); } catch (e) { return null; } };
const write = (k, v) => { try { window.localStorage.setItem(k, v); } catch (e) { /* storage blocked */ } };

function useWide(px = 1200) {
  const [wide, setWide] = useState(() => window.innerWidth >= px);
  useEffect(() => {
    const on = () => setWide(window.innerWidth >= px);
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, [px]);
  return wide;
}

// The selected job's match (header grade, Overview), polled while it's being scored
function useMatch(cid, jobId) {
  const [d, setD] = useState(null);
  const load = useCallback(() => (jobId ? api(`/api/candidates/${cid}/match/${jobId}`).then(setD).catch(() => setD({ status: 'failed' })) : Promise.resolve()), [cid, jobId]);
  useEffect(() => { setD(null); load(); }, [load]);
  useEffect(() => {
    if (!d || ['done', 'failed', 'not_scored'].includes(d.status)) return undefined;
    const t = setTimeout(load, 2500);
    return () => clearTimeout(t);
  }, [d, load]);
  return jobId ? d : null;
}

function EditDialog({ c, onClose, onDone }) {
  const [f, setF] = useState({ full_name: c.full_name || '', current_title: c.current_title || '', current_company: c.current_company || '',
    city: c.city || '', state: c.state || '', country: c.country || '', total_years: c.total_years ?? '', work_authorization: c.work_authorization || 'unknown',
    tags: (c.tags || []).join('; '), source_detail: c.source_detail || '' });
  const [error, setError] = useState('');
  const save = async () => {
    try {
      const body = { ...f, tags: f.tags.split(/[;,]/).map((t) => t.trim()).filter(Boolean), total_years: f.total_years === '' ? null : Number(f.total_years) };
      onDone(await api(`/api/candidates/${c.id}`, { method: 'PATCH', body }));
    } catch (e) { setError(e.message); }
  };
  const t = (k, label) => <label className="block"><span className={labelCls}>{label}</span><input className={input} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} /></label>;
  return (
    <Modal open title="Edit details" onClose={onClose} width="max-w-xl"
      footer={<><button className={btnSecondary} onClick={onClose}>Cancel</button><button className={btnPrimary} disabled={!f.full_name.trim()} onClick={save} data-testid="edit-save">Save</button></>}>
      <div className="grid sm:grid-cols-2 gap-3">
        {t('full_name', 'Full name')}{t('current_title', 'Current title')}{t('current_company', 'Current company')}
        <label className="block"><span className={labelCls}>Years of experience</span><input type="number" min={0} className={input} value={f.total_years} onChange={(e) => setF({ ...f, total_years: e.target.value })} /></label>
        {t('city', 'City')}{t('state', 'State')}{t('country', 'Country')}
        <label className="block"><span className={labelCls}>Work authorization</span>
          <select className={input} value={f.work_authorization} onChange={(e) => setF({ ...f, work_authorization: e.target.value })}>{WORK_AUTH.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></label>
        {t('tags', 'Tags (; separated)')}{t('source_detail', 'Source detail')}
      </div>
      <div className="mt-3"><InlineError>{error}</InlineError></div>
    </Modal>
  );
}

function DeleteDialog({ c, onClose }) {
  const navigate = useNavigate();
  const toast = useToast();
  const [check, setCheck] = useState(null);
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  useEffect(() => { api(`/api/candidates/${c.id}/delete-check`).then(setCheck).catch((e) => setError(e.message)); }, [c.id]);
  const go = async () => {
    try { await api(`/api/candidates/${c.id}`, { method: 'DELETE', body: { reason } }); toast.show('Candidate deleted'); navigate('/candidates'); }
    catch (e) { setError(e.message); }
  };
  return (
    <Modal open title={`Delete ${c.full_name}?`} onClose={onClose} width="max-w-md"
      footer={<><button className={btnSecondary} onClick={onClose}>Cancel</button><button className={btnDanger} disabled={reason.trim().length < 3} onClick={go} data-testid="delete-confirm">Delete permanently</button></>}>
      <p className="text-sm text-[#334155]">Their details, contacts, resumes, applications and activity are deleted for good (e.g. an erasure request). This can’t be undone.</p>
      {check && <p className="text-sm text-[#64748B] mt-2">{check.documents} document(s) · {check.applications} application(s)</p>}
      {check && check.linked_phase3_interviews.length > 0 && <p className="text-sm text-[#92400E] mt-2">{check.linked_phase3_interviews.length} Quali5d AI interview(s) stay in Technical Screen — delete those there if needed.</p>}
      <label className="block mt-3"><span className={labelCls}>Reason (recorded)</span><input className={input} value={reason} onChange={(e) => setReason(e.target.value)} maxLength={300} placeholder="e.g. GDPR erasure request" /></label>
      <div className="mt-3"><InlineError>{error}</InlineError></div>
    </Modal>
  );
}

function AddJobDialog({ c, onClose, onDone }) {
  const [job, setJob] = useState(null);
  const [error, setError] = useState('');
  return (
    <Modal open title={`Add ${c.full_name} to a job`} onClose={onClose} width="max-w-md"
      footer={<><button className={btnSecondary} onClick={onClose}>Cancel</button><button className={btnPrimary} disabled={!job}
        onClick={() => api(`/api/candidates/${c.id}/applications`, { method: 'POST', body: { requirement_id: job } }).then(onDone).catch((e) => setError(e.message))}>Add</button></>}>
      <JobPicker value={job} onChange={setJob} />
      <p className="text-xs text-[#64748B] mt-2">They join at “Sourced”. Adding doesn’t make you the owner — your first logged call or e-mail does.</p>
      <div className="mt-3"><InlineError>{error}</InlineError></div>
    </Modal>
  );
}

function PdfDialog({ c, jobId, onClose, onDone }) {
  const [hide, setHide] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const go = async () => {
    setBusy(true); setError('');
    try { await downloadProfilePdf(c.id, jobId, hide); onDone(); } catch (e) { setError(e.message); setBusy(false); }
  };
  return (
    <Modal open title="Download profile (PDF)" onClose={onClose} width="max-w-md"
      footer={<><button className={btnSecondary} onClick={onClose}>Cancel</button><button className={btnPrimary} disabled={busy} onClick={go} data-testid="pdf-confirm">{busy ? 'Preparing…' : 'Download PDF'}</button></>}>
      <p className="text-sm text-[#334155]">A one-to-two-page summary to share with a hiring manager: the match for this job, must-haves, key facts, experience, skills and the screen summary. Pay shows only if you can see it.</p>
      <label className="inline-flex items-center gap-2 text-sm mt-3"><input type="checkbox" checked={hide} onChange={(e) => setHide(e.target.checked)} data-testid="hide-contacts" /> Hide contact details (for sharing outside the team)</label>
      <p className="text-xs text-[#64748B] mt-2">Downloads are recorded.</p>
      <div className="mt-3"><InlineError>{error}</InlineError></div>
    </Modal>
  );
}

function Contacts({ contacts }) {
  return (
    <span className="flex flex-wrap gap-x-4 gap-y-1 text-sm" data-testid="contacts">
      {contacts.map((x) => (
        <span key={x.id} className={x.shared ? 'text-[#991B1B]' : ''} title={x.shared ? 'Also on a differently named candidate' : undefined}>
          {x.kind === 'linkedin' || x.kind === 'github' || x.kind === 'portfolio'
            ? <a href={x.value.startsWith('http') ? x.value : `https://${x.value}`} target="_blank" rel="noopener noreferrer">{x.kind === 'linkedin' ? 'LinkedIn' : x.kind === 'github' ? 'GitHub' : 'Portfolio'}</a>
            : x.value}{x.shared ? ' ▲' : ''}</span>
      ))}
    </span>
  );
}

function ProfileBody() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const admin = isAdmin();
  const wide = useWide();
  const [c, setC] = useState(null);
  const [error, setError] = useState(null);
  const [dialog, setDialog] = useState(null);         // the action bar's dialogs (screen, call, reject, …)
  const [pageDialog, setPageDialog] = useState(null);  // edit, delete, add to job, PDF
  const [merge, setMerge] = useState(null);
  const [panel, setPanel] = useState(() => read(PANEL_OPEN) !== '0');
  const [params, setParams] = useSearchParams();
  const load = useCallback(() => api(`/api/candidates/${id}`).then((d) => { setC(d); setError(null); }).catch((e) => setError(e)), [id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {                                    // #app-{id}: the job card the link points at
    const el = c && window.location.hash.startsWith('#app-') && document.getElementById(window.location.hash.slice(1));
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'start' });
  }, [c]);

  const apps = c ? c.applications.filter((a) => a.accessible !== false) : [];
  const hidden = c ? c.applications.length - apps.length : 0;
  const asked = Number(params.get('job')) || null;
  const options = apps.map((a) => ({ id: a.requirement_id, title: a.job_title }));
  if (asked && !options.some((x) => x.id === asked)) options.unshift({ id: asked, title: 'This search (not on the job yet)' });
  const jobId = asked || (options[0] && options[0].id) || null;
  const app = apps.find((a) => a.requirement_id === jobId) || null;
  const workable = !app || app.can_work !== false;    // the recruiting side (or the owner): the actions
  const searchable = !app || app.can_search !== false; // + the job's HM (4f): the match, the grade, the job's PDF
  const match = useMatch(id, searchable ? jobId : null);

  const tabs = [
    { key: 'overview', label: 'Overview' },
    jobId && searchable && { key: 'match', label: 'Match' },
    { key: 'profile', label: 'Profile' },
    { key: 'screens', label: 'Screens & interviews' },
    { key: 'authenticity', label: <>Authenticity{c && c.authenticity && c.authenticity.needs_verification > 0
      ? <span className="ml-1 text-[#DC2626]" data-testid="auth-count" title="Items needing verification">⚠ {c.authenticity.needs_verification}</span> : null}</> },
    !wide && { key: 'activity', label: 'Activity' },
  ].filter(Boolean);
  const keys = tabs.map((t) => t.key);
  const want = params.get('tab') || read(LAST_TAB) || 'overview';
  const tab = keys.includes(want) ? want : 'overview';
  const [visited, setVisited] = useState(() => new Set());
  if (!visited.has(tab)) setVisited(new Set([...visited, tab]));
  const setTab = useCallback((k) => {
    write(LAST_TAB, k);
    setParams((p) => { const n = new URLSearchParams(p); n.set('tab', k); return n; }, { replace: true });
  }, [setParams]);
  const setJob = (j) => setParams((p) => { const n = new URLSearchParams(p); n.set('job', String(j)); return n; }, { replace: true });
  useEffect(() => {                                    // keys 1–5 (6 = Activity when it's a tab), outside fields and dialogs
    const onKey = (e) => {
      if (e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;
      const t = e.target;
      if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return;
      if (document.querySelector('[role="dialog"]')) return;
      const n = Number(e.key);
      const order = TAB_KEYS.filter((k) => keys.includes(k));
      if (n >= 1 && n <= order.length) { e.preventDefault(); setTab(order[n - 1]); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [keys.join(), setTab]); // eslint-disable-line react-hooks/exhaustive-deps
  const togglePanel = () => { write(PANEL_OPEN, panel ? '0' : '1'); setPanel(!panel); };

  if (error) {
    return (
      <div className={card + ' p-6'}>
        <InlineError>{error.status === 404 ? 'This candidate doesn’t exist or isn’t in your company.' : `${error.message}${error.reference ? ` (reference ${error.reference})` : ''}`}</InlineError>
        <button className={btnSecondary + ' mt-3'} onClick={() => navigate('/candidates')}>← Candidates</button>
      </div>
    );
  }
  if (!c) return <div className={card + ' p-6 space-y-3 min-h-[220px]'} aria-busy="true"><Skeleton className="h-6 w-1/3" /><Skeleton /><Skeleton /><Skeleton className="h-8 w-2/3" /></div>;

  const pool = () => api(`/api/candidates/${id}`, { method: 'PATCH', body: { talent_pool: !c.talent_pool } }).then((d) => { setC(d); toast.show(d.talent_pool ? 'Added to the talent pool' : 'Removed from the talent pool'); })
    .catch((e) => toast.show(e.message, 'error'));
  const privacy = () => downloadPrivacyData(c).then(() => { toast.show('Downloaded — document links in the file work for 5 minutes'); load(); }).catch((e) => toast.show(e.message, 'error'));
  const extra = [
    { key: 'pool', label: c.talent_pool ? 'Remove from talent pool' : 'Add to talent pool', onClick: pool },
    { key: 'pdf', label: 'Download profile (PDF)…', onClick: () => setPageDialog('pdf') },
    admin && { key: 'data', label: 'Download data (privacy request)', onClick: privacy },
    { key: 'edit', label: 'Edit details…', onClick: () => setPageDialog('edit') },
    admin && { key: 'delete', label: 'Delete candidate…', danger: true, onClick: () => setPageDialog('delete') },
  ].filter(Boolean);
  const showPanel = wide && panel;
  const openScreen = () => setDialog('screen');
  const body = {
    overview: <OverviewTab c={c} jobId={jobId} app={app} match={match} viewOnly={!searchable} apps={apps} hidden={hidden} onTab={setTab} onJob={setJob} onOpenScreen={workable ? openScreen : null} />,
    match: jobId && searchable && <MatchPanel candidateId={c.id} jobId={jobId} jobs={[]} onJob={setJob} />,
    profile: <ProfileTab c={c} />,
    screens: <ScreensTab app={app} onOpenScreen={workable ? openScreen : null} onAlex={workable && app ? setDialog : null} />,
    authenticity: <AuthenticityPanel candidateId={c.id} />,
    activity: <ActivityPanel c={c} appId={app && app.id} onAdded={load} />,
  };

  return (
    <div data-testid="candidate-profile">
      <header className={card + ' sticky top-0 z-20 p-4 sm:p-5 shadow-sm'} data-testid="profile-header">
        <div className="flex items-start gap-3">
          {match && match.status === 'done' && <GradeTile grade={match.grade} total={match.total} />}
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-2xl font-extrabold text-[#0F172A] mr-1">{c.full_name}</h1>
              {c.authenticity && <RiskBadge risk={c.authenticity.risk} />}
              {app && <span className="text-xs text-[#64748B]" data-testid="owner-text">{ownerText(app)}</span>}
            </div>
            <p className="text-sm text-[#64748B] mt-0.5">{[c.current_title, c.current_company, place(c), c.total_years != null ? `${c.total_years} yrs` : null].filter(Boolean).join(' · ') || '—'}</p>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1.5"><Contacts contacts={c.contacts || []} />
              <Chip>{c.candidate_code}</Chip><Chip>{SOURCE_LABEL[c.source] || c.source}{c.source_detail ? ` · ${c.source_detail}` : ''}</Chip></div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-3">
          {options.length > 0 && (
            <label className="inline-flex items-center gap-2 text-sm"><span className="text-[#64748B]">Viewing for:</span>
              <select className={input + ' w-auto py-1.5'} value={jobId || ''} onChange={(e) => setJob(Number(e.target.value))} data-testid="job-switcher">
                {options.map((o) => <option key={o.id} value={o.id}>{o.title}</option>)}</select></label>
          )}
          {app && <Stepper steps={(app.progress && app.progress.steps) || []} className="" />}
        </div>
        {app && <AppStatus app={app} />}
        <div className="mt-3">
          {app ? <ActionBar app={app} candidate={c} onChanged={load} dialog={dialog} setDialog={setDialog} extra={extra} />
            : (
              <div className="flex flex-nowrap items-center gap-2 whitespace-nowrap" data-testid="action-bar">
                <button className={btnPrimary + ' shrink-0'} onClick={() => setPageDialog('job')} data-testid="next-step">Add to job</button>
                <button className={iconBtn} aria-label="Note" title="Note" onClick={() => setPageDialog('note')}>📝</button>
                <MoreMenu items={extra} />
              </div>
            )}
        </div>
        {app && <TransferBanner app={app} onChanged={load} />}
      </header>

      {(c.flags || []).filter((f) => f.kind !== 'synthetic_planted').length > 0 && (
        <div className="mt-4 rounded-xl bg-[#FEF2F2] border border-[#FECACA] text-[#7F1D1D] px-4 py-3 text-sm" data-testid="flags">
          <b>▲ Authenticity: review before contacting.</b>{' '}
          {c.flags.filter((f) => f.kind !== 'synthetic_planted').map((f, i) => (
            f.kind === 'interview_email'
              ? <span key={i} data-testid="flag-interview-email">Interview e-mail belongs to {f.related_code ? <button className="underline" onClick={() => navigate(`/candidates/${f.related_candidate_id}`)}>{f.related_code}</button> : 'another candidate'} — check. </span>
              : <span key={i}>{f.kind === 'shared_resume' ? 'The same resume' : `The same ${f.via}`} is on another person{f.related_code ? <> (<button className="underline" onClick={() => navigate(`/candidates/${f.related_candidate_id}`)}>{f.related_code}</button>)</> : ''}. </span>
          ))}
        </div>
      )}
      {(c.suggestions || []).length > 0 && (
        <div className="mt-3 rounded-xl bg-[#FFFBEB] border border-[#FDE68A] px-4 py-3 text-sm flex flex-wrap items-center gap-2" data-testid="suggestions">
          <DuplicateBadges tiers={[...new Set(c.suggestions.map((s) => s.tier).filter((t) => t !== 'definite'))]} />
          <span className="flex-1">{c.suggestions.some((s) => s.tier === 'definite') ? 'New details differ from what we have.' : 'This may be the same person as another record.'}</span>
          {c.suggestions.map((s) => <button key={s.id} className={btnSecondary} onClick={() => setMerge(s.id)}>{s.other_candidate_id ? 'Compare' : 'Review details'}</button>)}
        </div>
      )}

      <div className={`grid gap-4 mt-4 items-start ${showPanel ? 'grid-cols-[minmax(0,1fr)_340px]' : 'grid-cols-[minmax(0,1fr)_auto]'}`}>
        <div className="min-w-0">
          <div className="flex items-center gap-2"><div className="flex-1 min-w-0 overflow-x-auto"><Tabs tabs={tabs} active={tab} onChange={setTab} label="Candidate profile" /></div></div>
          {tabs.map((t) => (visited.has(t.key) || t.key === tab) && (
            <div key={t.key} role="tabpanel" id={`panel-${t.key}`} aria-labelledby={`tab-${t.key}`} hidden={t.key !== tab} className="pt-4" data-testid={`panel-${t.key}`}>
              {body[t.key]}
            </div>
          ))}
        </div>
        {wide && (showPanel ? (
          <aside className="space-y-2" data-testid="activity-aside">
            <button type="button" className="text-xs font-semibold text-[#64748B] hover:text-[#0F172A]" onClick={togglePanel} aria-expanded="true" data-testid="activity-toggle">Hide activity ›</button>
            <ActivityPanel c={c} appId={app && app.id} onAdded={load} />
          </aside>
        ) : (
          <button type="button" className={btnSecondary + ' text-xs py-1.5'} onClick={togglePanel} aria-expanded="false" data-testid="activity-toggle">‹ Activity</button>
        ))}
      </div>

      {pageDialog === 'edit' && <EditDialog c={c} onClose={() => setPageDialog(null)} onDone={(d) => { setC(d); setPageDialog(null); toast.show('Saved'); }} />}
      {pageDialog === 'delete' && <DeleteDialog c={c} onClose={() => setPageDialog(null)} />}
      {pageDialog === 'job' && <AddJobDialog c={c} onClose={() => setPageDialog(null)} onDone={(a) => { setPageDialog(null); toast.show('Added to the job'); if (a && a.requirement_id) setJob(a.requirement_id); load(); }} />}
      {pageDialog === 'note' && <LogDialog kind="note" app={null} candidate={c} onClose={() => setPageDialog(null)} onDone={() => { setPageDialog(null); load(); }} />}
      {pageDialog === 'pdf' && <PdfDialog c={c} jobId={searchable ? jobId : null} onClose={() => setPageDialog(null)} onDone={() => { setPageDialog(null); toast.show('Profile PDF downloaded'); load(); }} />}
      {merge && <MergeDialog id={merge} onClose={() => setMerge(null)} onDone={(r) => { setMerge(null); if (r && r.id && String(r.id) !== String(id)) navigate(`/candidates/${r.id}`); else load(); }} />}
    </div>
  );
}

// Without candidate access: the candidate's review page on a job this person works or hires for, else say so
function AccessGate() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [denied, setDenied] = useState(false);
  useEffect(() => {
    const job = params.get('job');
    api(`/api/candidates/${id}/access${job ? `?job=${encodeURIComponent(job)}` : ''}`)
      .then((d) => { if (d.redirect) navigate(d.redirect, { replace: true }); else setDenied(true); })
      .catch(() => setDenied(true));
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!denied) return <div className={card + ' p-6 space-y-2'} aria-busy="true"><Skeleton /><Skeleton /></div>;
  return (
    <div className={card + ' p-6'} data-testid="no-access">
      <h1 className="text-lg font-extrabold text-[#0F172A]">You don’t have access to this candidate</h1>
      <p className="text-sm text-[#64748B] mt-1">Ask the job’s recruiter or hiring manager if you need to see this profile.</p>
      <button className={btnSecondary + ' mt-4'} onClick={() => navigate('/jobs')}>Back to My jobs</button>
    </div>
  );
}

export default function CandidateProfilePage() {
  const navigate = useNavigate();
  const allowed = canUseCandidates(storedSession().user);
  useEffect(() => {
    if (!localStorage.getItem('token')) navigate('/');
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return <ToastProvider><AppShell active={allowed ? 'candidates' : 'jobs'}>{allowed ? <ProfileBody /> : <AccessGate />}</AppShell></ToastProvider>;
}
